import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Truck } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { ddmmyyyy, num } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../setup/ConfirmDialog";
import { ChallanShare, MinCell, StockAlerts } from "./ChallanShare";
import { RefillSuggestions } from "./StockExtras";
import { ExportButton } from "../ExportButton";

const ChallanRow = ({ c, isAdmin, canWrite, onDelete, company }) => (
  <div className="panel space-y-1 p-4" data-testid={`challan-${c.dc_no}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="mono font-bold">{c.dc_no}</span>
      <span className={`chip ${c.status === "Received" ? "chip-Accepted" : "chip-lr chip-lr-in-transit"}`} data-testid={`challan-${c.dc_no}-status`}>{c.status}</span>
    </div>
    <p className="text-sm font-semibold">{c.from_location} → {c.to_location}</p>
    <p className="text-xs text-muted-foreground">{ddmmyyyy(c.date)}{c.vehicle ? ` · ${c.vehicle}` : ""} · {num(c.qty_total)} shirts
      {c.status === "Received" && ` · received ${num(c.received_total)}${c.short_total ? `, short ${num(c.short_total)}` : ""} on ${ddmmyyyy(c.received_date)}`}</p>
    <p className="text-xs text-muted-foreground">{c.items.map((i) => `${i.description}${i.style ? ` (${i.style})` : ""} × ${num(i.qty)}`).join(", ")}</p>
    <ChallanShare c={c} company={company} />
    {c.status === "In Transit" && canWrite && (
      <div className="grid grid-cols-2 gap-2 pt-2">
        <Link to={`/challans/${c.id}/receive`} className="btn btn-accent" data-testid={`challan-${c.dc_no}-receive-btn`}>Receive</Link>
        {isAdmin ? <button className="btn btn-outline btn-ghost-danger" onClick={() => onDelete(c)} data-testid={`challan-${c.dc_no}-delete-btn`}><Trash2 size={16} /> Delete</button> : <span />}
      </div>
    )}
  </div>
);

export const ShirtStock = () => {
  const { isAdmin, can } = useAuth();
  const canWrite = can("stock_write");
  const [stock, setStock] = useState(null);
  const [challans, setChallans] = useState([]);
  const [del, setDel] = useState(null);
  const [tick, setTick] = useState(0);
  const [company, setCompany] = useState(undefined);
  const [kind, setKind] = useState("All");
  useEffect(() => {
    api.get("/settings").then((r) => setCompany(r.data.company_name)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    Promise.all([api.get("/fg-stock"), api.get("/challans")])
      .then(([s, c]) => { setStock(s.data); setChallans(c.data); setTick((t) => t + 1); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!stock) return <p className="text-muted-foreground">Loading…</p>;
  const match = (it) => kind === "All" || (it.kind || "Shirts") === kind;
  const locations = stock.locations.map((l) => {
    const items = l.items.filter(match);
    return { ...l, items, total: items.reduce((a, i) => a + i.qty, 0) };
  }).filter((l) => l.items.length);

  const remove = async () => {
    const c = del;
    setDel(null);
    try {
      await api.delete(`/challans/${c.id}`);
      toast.success(`${c.dc_no} deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="shirt-stock">
      {canWrite && <div className="grid grid-cols-2 gap-2">
        <Link to="/challans/new" className="btn btn-primary" data-testid="new-challan-btn"><Truck size={18} /> New challan</Link>
        <Link to="/sales/new" className="btn btn-outline" data-testid="record-sale-btn">Record sale</Link>
      </div>}
      <div className="seg" data-testid="stock-kind-filter">
        {["All", "Shirts", "Pants"].map((k) => <button key={k} data-active={kind === k} onClick={() => setKind(k)} data-testid={`stock-kind-${k.toLowerCase()}`}>{k}</button>)}
      </div>
      <StockAlerts alerts={stock.alerts.filter(match)} />
      <RefillSuggestions reloadKey={tick} />
      <div className="space-y-3">
        <h2 className="mono-label px-1 text-muted-foreground">{kind === "All" ? "Shirts & pants" : kind} in stock by location</h2>
        <div className="grid grid-cols-2 gap-2"><ExportButton name="stock" label="Stock Excel" /><ExportButton name="challans" label="Challans Excel" /></div>
        {canWrite && <p className="px-1 text-xs text-muted-foreground">Tap "Set min" on an item to get an alert when that location runs low.</p>}
        {!locations.length && <p className="panel p-6 text-center text-muted-foreground" data-testid="shirt-stock-empty">Nothing in stock yet. Shirts arrive from fabricators; pants arrive through a Pants PO's GRN.</p>}
        {locations.map((l) => (
          <div key={l.location} className="panel p-4" data-testid={`stock-location-${l.location}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-heading font-bold">{l.location}{l.kind && <span className="ml-2 text-xs font-normal text-muted-foreground">{l.kind}</span>}</p>
              <p className="mono font-bold text-brand" data-testid={`stock-location-${l.location}-total`}>{num(l.total)}</p>
            </div>
            <div className="divide-y">
              {l.items.map((it) => (
                <div key={it.key} className="flex justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">{it.description} {it.code && <span className="mono text-xs text-muted-foreground">[{it.code}]</span>}
                    {it.style && <span className="block truncate text-xs text-muted-foreground">{it.style}</span>}</span>
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <span className={`mono font-semibold ${it.min && it.qty < it.min ? "text-destructive" : ""}`}>{num(it.qty)}</span>
                    {canWrite ? <MinCell location={l.location} item={it} onSaved={load} /> : !!it.min && <span className="text-xs text-muted-foreground">min {num(it.min)}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <h2 className="mono-label px-1 text-muted-foreground">Delivery challans</h2>
        {!challans.length && <p className="panel p-4 text-center text-sm text-muted-foreground">No challans yet.</p>}
        {challans.map((c) => <ChallanRow key={c.id} c={c} isAdmin={isAdmin} canWrite={canWrite} onDelete={setDel} company={company} />)}
      </div>
      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove} title={`Delete ${del?.dc_no}?`} body="The shirts go back to the sending location's stock." />
    </div>
  );
};

export const AddLine = ({ onClick }) => (
  <button type="button" className="btn btn-outline w-full border-dashed" onClick={onClick} data-testid="challan-add-line-btn"><Plus size={18} /> Add item</button>
);
